import { useCallback, useState } from 'react'
import { LinkResponse, Recipient, TransportType } from '@/interfaces/invite'
import { InviteController } from '@/controllers'
import { useNotification } from '@/hooks/useNotification'
import { StorageFactory } from '@/services'
import { StorageType } from '@/interfaces/storage'
import { useParticipants } from '@/hooks/useParticipants'
import { GTM_EVENT } from '@/interfaces/gtm'
import { useGtm } from '@/hooks/useGtm'
import { NotificationType } from '@/components/ui'

export const useInviteLinks = () => {
  const [fbInvitationLinks, setFBInvitationLinks] = useState<LinkResponse | null>(null)
  const [copyInvitationLinks, setCopyInvitationLinks] = useState<LinkResponse | null>(null)
  const [textInvitationLinks, setTextInvitationLinks] = useState<LinkResponse | null>(null)
  const [emailInvitationLinks, setEmailInvitationLinks] = useState<LinkResponse | null>(null)
  const { openNotification } = useNotification()
  const { participants: participantList, isLoading: participantLoading } = useParticipants()
  const { sendDataToGTM } = useGtm()
  const localStorage = StorageFactory.getStorage(StorageType.LOCAL)

  const prepareShortLink = (shortLink: string) => `${shortLink}?ref=send`

  const loadLink = async ({
    participantIds,
    recipient,
    force,
  }: {
    participantIds: number[]
    recipient: Recipient
    force?: boolean
  }) => {
    const needCaching = recipient.address === '' && recipient.type !== TransportType.FB
    const cacheKey = `__charleston_invite_${participantIds.join('-')}_${recipient.type}`

    if (needCaching && !force) {
      const links = localStorage.getItem<LinkResponse>(cacheKey)

      if (links) {
        return links
      }
    }
    const {
      result: [error, links],
    } = await InviteController.getInvitationLink(participantIds, recipient)

    if (error) {
      openNotification(error, NotificationType.ERROR)

      return null
    }

    localStorage.setItem(cacheKey, links)

    return links
  }

  const loadFbInvitationLink = async ({
    participantIds,
    force,
    imageFile,
  }: {
    participantIds: number[]
    force?: boolean
    imageFile?: { bucket: string; objectName: string }
  }) => {
    if (fbInvitationLinks && !force) {
      return fbInvitationLinks
    }

    const recipient: Recipient = {
      type: TransportType.FB,
      name: '',
      address: '',
      imageFile,
    }
    const links = await loadLink({ participantIds, recipient, force })

    setFBInvitationLinks(links)

    return links
  }

  const loadCopyInvitationLink = async ({ participantIds }: { participantIds: number[] }) => {
    if (copyInvitationLinks) {
      return copyInvitationLinks
    }

    const recipient: Recipient = {
      type: TransportType.COPY,
      name: '',
      address: '',
    }
    const links = await loadLink({ participantIds, recipient })

    setCopyInvitationLinks(links)

    return links
  }

  const loadTextInvitationLink = async ({ participantIds }: { participantIds: number[] }) => {
    if (textInvitationLinks) {
      return textInvitationLinks
    }

    const recipient: Recipient = {
      type: TransportType.TEXT,
      name: '',
      address: '',
    }
    const links = await loadLink({ participantIds, recipient })

    setTextInvitationLinks(links)

    return links
  }

  const loadEmailInvitationLink = async ({ participantIds }: { participantIds: number[] }) => {
    if (emailInvitationLinks) {
      return emailInvitationLinks
    }

    const recipient: Recipient = {
      type: TransportType.EMAIL,
      name: '',
      address: '',
    }
    const links = await loadLink({ participantIds, recipient })

    setEmailInvitationLinks(links)

    return links
  }

  const loadInvitationLink = useCallback(
    async (
      transportType: TransportType,
      participantIds?: number[],
      options?: {
        force?: boolean
        imageFile?: { bucket: string; objectName: string }
      },
    ) => {
      if (participantLoading && !participantIds) {
        return
      }

      const _participantIds =
        participantIds || participantList.filter(({ disabled }) => !disabled).map((participant) => participant.id)
      switch (transportType) {
        case TransportType.COPY:
          return loadCopyInvitationLink({
            participantIds: _participantIds,
          })
        case TransportType.FB:
          return loadFbInvitationLink({
            participantIds: _participantIds,
            force: options?.force,
            imageFile: options?.imageFile,
          })
        case TransportType.TEXT:
          return loadTextInvitationLink({
            participantIds: _participantIds,
          })
        case TransportType.EMAIL:
          return loadEmailInvitationLink({
            participantIds: _participantIds,
          })
        default:
          throw new Error(`Transport type [${transportType}] cannot be resolved`)
      }
    },
    [participantLoading],
  )

  const sendTextClickEvent = () => {
    sendDataToGTM({ event: GTM_EVENT.CLICK_SEND_TEXT })
  }

  const sendEmailClickEvent = () => {
    sendDataToGTM({ event: GTM_EVENT.CLICK_SEND_EMAIL })
  }

  return {
    fbInvitationLinks,
    textInvitationLinks,
    copyInvitationLinks,
    emailInvitationLinks,
    loadInvitationLink,
    sendTextClickEvent,
    sendEmailClickEvent,
    prepareShortLink,
  }
}
